import { useState, useEffect } from "react";
import { Menu, ArrowUpRight } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "../components/ui/sheet";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Services", href: "#services", route: "/services" },
  { label: "About", href: "#about", route: "/about" },
  { label: "Portfolio", href: "#portfolio", route: "/portfolio" },
  { label: "Testimonials", href: "#testimonials", route: "/testimonials" },
  { label: "Contact", href: "#contact", route: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (link) => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.querySelector(link.href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(link.route);
    }
  };

  const goHome = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const goContact = () => {
    setMobileOpen(false);
    if (isHome) {
      const el = document.querySelector("#contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/contact");
    }
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "backdrop-blur-xl bg-slate-950/80 border-b border-white/5 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={goHome}
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo.png"
              alt="Softlume Logo"
              className="h-12 w-auto group-hover:drop-shadow-[0_0_15px_rgba(59,130,246,0.5)] transition-all duration-300"
            />
            {/* <span className="font-heading font-bold text-xl text-white tracking-tight">
              Soft<span className="text-blue-400">lume</span>
            </span> */}
          </button>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                onClick={() => handleNav(link)}
                className={`px-4 py-2 text-sm transition-colors duration-300 rounded-lg hover:bg-white/5 ${
                  location.pathname === link.route
                    ? "text-blue-400"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {link.label}
              </button>
            ))}
            <Button
              data-testid="nav-cta-button"
              onClick={goContact}
              className="ml-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 h-9 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)]"
            >
              Get Started
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="mobile-menu-button"
                  className="text-white hover:bg-white/10"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-slate-950 border-l border-white/5 w-[280px]"
              >
                <SheetTitle className="text-white font-heading">
                  Menu
                </SheetTitle>
                <div className="flex flex-col gap-2 mt-8">
                  {navLinks.map((link) => (
                    <button
                      key={link.label}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      onClick={() => handleNav(link)}
                      className="text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors text-base"
                    >
                      {link.label}
                    </button>
                  ))}
                  <Button
                    data-testid="mobile-cta-button"
                    onClick={goContact}
                    className="mt-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full h-10"
                  >
                    Get Started
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.nav>
  );
}
